import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: 'polling', planName: null, message: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', planName: null, message: 'Missing session id.' });
      return;
    }
    let dead = false;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (dead) return;
        if (data.payment_status === 'paid') {
          await refresh().catch(() => {});
          setState({ status: 'paid', planName: data.plan_name, message: null });
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'failed') {
          setState({ status: 'failed', planName: data.plan_name, message: 'Payment failed or expired.' });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({
            status: 'timeout',
            planName: data.plan_name,
            message: 'We are still processing your payment. We will email a receipt as soon as it confirms.',
          });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (dead) return;
        setState({ status: 'error', planName: null, message: e?.response?.data?.detail || 'status_check_failed' });
      }
    };

    tick();
    return () => { dead = true; };
  }, [sessionId, refresh]);

  const renderBody = () => {
    if (state.status === 'polling') {
      return (
        <>
          <Loader2 size={48} className="gold-text animate-spin" />
          <h1 className="mt-8 font-display text-3xl sm:text-4xl font-bold text-[#F1ECDF]">Finalizing your subscription...</h1>
          <p className="mt-3 text-sm text-[#CDBE94]">Hold tight — confirming with Stripe.</p>
        </>
      );
    }
    if (state.status === 'paid') {
      return (
        <>
          <CheckCircle2 size={56} className="pnl-positive" />
          <h1 className="mt-8 font-display text-3xl sm:text-4xl font-bold text-[#F1ECDF]">
            Welcome to <span className="gold-text">{state.planName || 'Talas'}</span>.
          </h1>
          <p className="mt-3 text-sm text-[#CDBE94]">
            Your account is now <span className="pnl-positive font-semibold">paid</span>. Connect your broker
            and head to your dashboard to start receiving signals.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/dashboard"
              data-testid="billing-success-go-dashboard"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-sm text-sm font-medium bg-[#C9A24B] text-[#1a1407] hover:bg-[#E8C874] transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        </>
      );
    }
    if (state.status === 'timeout') {
      return (
        <>
          <Loader2 size={48} className="gold-text" />
          <h1 className="mt-8 font-display text-3xl sm:text-4xl font-bold text-[#F1ECDF]">Still processing</h1>
          <p className="mt-3 text-sm text-[#CDBE94]">{state.message}</p>
          <Link
            to="/dashboard"
            data-testid="billing-timeout-go-dashboard"
            className="mt-8 inline-flex items-center justify-center gap-2 py-3 px-6 rounded-sm text-sm font-medium border border-[rgba(201,162,75,0.4)] text-[#F1ECDF] hover:border-[#E8C874] hover:bg-[#C9A24B]/10 transition-colors"
          >
            Continue to Dashboard
          </Link>
        </>
      );
    }
    return (
      <>
        <AlertCircle size={48} className="pnl-negative" />
        <h1 className="mt-8 font-display text-3xl sm:text-4xl font-bold text-[#F1ECDF]">Something went wrong</h1>
        <p className="mt-3 text-sm text-[#CDBE94]">{state.message || 'Please try again or contact support.'}</p>
        <Link
          to="/pricing"
          data-testid="billing-error-back-pricing"
          className="mt-8 inline-flex items-center justify-center gap-2 py-3 px-6 rounded-sm text-sm font-medium border border-[rgba(201,162,75,0.4)] text-[#F1ECDF] hover:border-[#E8C874] hover:bg-[#C9A24B]/10 transition-colors"
        >
          Back to Pricing
        </Link>
      </>
    );
  };

  return (
    <div
      data-testid="billing-success-page"
      className="min-h-[60vh] flex items-center justify-center px-6 py-24"
    >
      <div className="text-center max-w-xl">
        <div className="flex justify-center">{renderBody()}</div>
      </div>
    </div>
  );
}
